import { getCampaignLeadCostQueryOptions } from "@/api/campaign.api";
import { creditsQueryOptions } from "@/api/credits.api";
import {
  MaxLeadsPerWeekAndPeriodForm,
  maxLeadsPerWeekAndPeriodFormSchema,
} from "@/components/campaign/setup/planning/MaxLeadsPerWeekAndPeriodForm";
import {
  createSchedulesAndPeriodFormSchema,
  SchedulesAndPeriodForm,
} from "@/components/campaign/setup/planning/SchedulesAndPeriodForm";
import { AlertModal } from "@/components/generic/AlertModal";
import { Button } from "@/components/generic/Button";
import { PageHeaderProvider } from "@/providers/PageHeaderProvider";
import { getCampaignUpdateCreditStats } from "@/services/credits.service";
import { NewCampaign } from "@/types/campaign";
import { entriesToRecord, mapAndExclude } from "@/utils/array-utils";
import { mergeScheduleUpdates } from "@/utils/campaign-helpers";
import { WEEKDAYS } from "@/utils/dates";
import {
  StepperSubmitHandler,
  useStepperSubmitHandlerState,
} from "@/utils/stepper";
import { getLocalTimeZone, today } from "@internationalized/date";
import {
  createFileRoute,
  HistoryState,
  useBlocker,
} from "@tanstack/react-router";
import { useMemo } from "react";
import { DateValue } from "react-aria";
import z from "zod";
import { Route as BuyCreditsRoute } from "./buy-credits";

const historyStateSchema = z
  .union([
    z.object({
      type: z.literal("Scheduled"),
      values: createSchedulesAndPeriodFormSchema(),
    }),
    z.object({
      type: z.literal("Triggered"),
      values: maxLeadsPerWeekAndPeriodFormSchema,
    }),
  ])
  .and(
    z.object({
      autoSubmit: z.boolean(),
    }),
  )
  .optional()
  .catch(undefined);

function parseHistoryState(historyState: z.infer<typeof historyStateSchema>) {
  return historyStateSchema.parse(historyState);
}

export const Route = createFileRoute(
  "/_dashboard/campaigns/$id/(edit)/planning/lead-amount-and-period/",
)({
  component: RouteComponent,
  beforeLoad: ({ location }) => {
    const historyState = parseHistoryState(
      location.state.routeStates?.[Route.id],
    );
    return {
      parseHistoryState,
      useHistoryState: () => historyState,
    };
  },
});

const getPeriodValues = ({
  chosenStartDate,
  chosenEndDate,
}: {
  chosenStartDate: DateValue | null;
  chosenEndDate: DateValue | null;
}) => ({
  startDate: (chosenStartDate ?? today(getLocalTimeZone()))?.toString(),
  endDate: chosenEndDate?.toString() ?? null,
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const [submitHandler, setSubmitHandler] = useStepperSubmitHandlerState();

  const Component = useMemo(() => {
    switch (context.stepperContext.planning?.type) {
      case "Scheduled":
        return ScheduledRouteComponent;
      case "Triggered":
        return TriggeredRouteComponent;
      default:
        throw Error("Campaign type not supported by this route");
    }
  }, [context.stepperContext.planning?.type]);

  const blocker = useBlocker({
    shouldBlockFn: ({ next }) => {
      if (
        next.fullPath === Route.fullPath ||
        next.fullPath === BuyCreditsRoute.fullPath
      )
        return false;
      return submitHandler.isDirty ?? false;
    },
    withResolver: true,
  });

  return (
    <PageHeaderProvider
      options={{
        actions: (
          <>
            <Button
              isDisabled={submitHandler.isDisabled || !submitHandler.isDirty}
              isPending={submitHandler.isPending}
              onPress={() => submitHandler.submit()}
            >
              Gem
            </Button>
          </>
        ),
      }}
    >
      <Component setSubmitHandler={setSubmitHandler} />

      <AlertModal
        isOpen={blocker.status === "blocked"}
        title="Du har ikke-gemte ændringer"
        description="Dine ændringer vil gå tabt. Er du sikker på at du vil fortsætte?"
        onClose={blocker.reset}
        onConfirm={() => {
          blocker.proceed?.();
        }}
      />
    </PageHeaderProvider>
  );
}

function ScheduledRouteComponent({
  setSubmitHandler,
}: {
  setSubmitHandler: (submitHandler: StepperSubmitHandler) => void;
}) {
  const navigate = Route.useNavigate();
  const context = Route.useRouteContext();
  const { campaign, updateCampaign } = context.useEditCampaign();
  const stepper = context.usePlanningStepper();

  const planningContext = useMemo(
    () =>
      stepper.context.planning?.type === "Scheduled"
        ? stepper.context.planning
        : null,
    [stepper.context],
  );

  if (!planningContext) throw null;

  let historyState = context.useHistoryState();
  if (historyState?.type !== "Scheduled") historyState = undefined;

  const defaultValues = useMemo(() => {
    const amountsByDayOfWeek = new Map(
      planningContext.schedules.map((schedule) => [
        schedule.dayOfWeek.toLowerCase(),
        schedule.amount,
      ]),
    );

    return {
      schedules: entriesToRecord(
        WEEKDAYS.map((day) => [day.id, amountsByDayOfWeek.get(day.id) ?? null]),
      ),
      period: getPeriodValues(planningContext),
    };
  }, [planningContext]);

  const values = useMemo(
    () => historyState?.values ?? defaultValues,
    [defaultValues, historyState?.values],
  );

  return (
    <SchedulesAndPeriodForm
      defaultValues={defaultValues}
      values={values}
      onSubmit={async (values) => {
        const updateSchedules = mapAndExclude(
          Object.entries(values.schedules),
          (exclude, [day, amount]) =>
            amount === null
              ? exclude
              : {
                  dayOfWeek: day,
                  amount,
                },
        );

        const chosenStartDate = values.period.startDate;
        const chosenEndDate = values.period.endDate;

        if (!historyState?.autoSubmit && campaign.status === "Active") {
          const campaignWithNewData: NewCampaign = {
            ...campaign,
            schedules: mergeScheduleUpdates(
              campaign.schedules,
              updateSchedules,
            ),
            chosenStartDate,
            chosenEndDate,
          };

          const [credits, campaignLeadCost, updatedCampaignLeadCost] =
            await Promise.all([
              context.queryClient.ensureQueryData(creditsQueryOptions),
              context.queryClient.ensureQueryData(
                getCampaignLeadCostQueryOptions(campaign),
              ),
              context.queryClient.ensureQueryData(
                getCampaignLeadCostQueryOptions(campaignWithNewData),
              ),
            ]);

          const {
            updatedCreditsNeededRestOfPeriod,
            updatedCreditsNeededNextPeriod,
          } = getCampaignUpdateCreditStats({
            credits,
            campaignLeadCost,
            updatedCampaignLeadCost,
          });

          const neededCredits = Math.max(
            updatedCreditsNeededRestOfPeriod,
            updatedCreditsNeededNextPeriod,
          );

          if (neededCredits) {
            const state = {
              routeStates: {
                [Route.id]: {
                  type: "Scheduled",
                  values: values,
                  autoSubmit: false,
                },
                [BuyCreditsRoute.id]: {
                  neededCredits,
                },
              },
            } satisfies HistoryState;

            await navigate({
              replace: true,
              state,
            });

            await navigate({
              to: BuyCreditsRoute.to,
              state,
            });
            return;
          }
        }

        await updateCampaign({
          schedules: updateSchedules,
          chosenStartDate,
          chosenEndDate,
        });
      }}
      setSubmitHandler={setSubmitHandler}
      autoSubmit={historyState?.autoSubmit}
    />
  );
}

function TriggeredRouteComponent({
  setSubmitHandler,
}: {
  setSubmitHandler: (submitHandler: StepperSubmitHandler) => void;
}) {
  const navigate = Route.useNavigate();
  const context = Route.useRouteContext();
  const { campaign, updateCampaign } = context.useEditCampaign();
  const stepper = context.usePlanningStepper();

  const planningContext = useMemo(
    () =>
      stepper.context.planning?.type === "Triggered"
        ? stepper.context.planning
        : null,
    [stepper.context],
  );

  if (!planningContext) throw null;

  let historyState = context.useHistoryState();
  if (historyState?.type !== "Triggered") historyState = undefined;

  const defaultValues = useMemo(() => {
    return {
      maxLeadsPerWeek: planningContext.maxLeadsPerWeek,
      period: getPeriodValues(planningContext),
    };
  }, [planningContext]);

  const values = useMemo(
    () => historyState?.values ?? defaultValues,
    [defaultValues, historyState?.values],
  );

  return (
    <MaxLeadsPerWeekAndPeriodForm
      defaultValues={defaultValues}
      values={values}
      onSubmit={async (values) => {
        const chosenStartDate = values.period.startDate;
        const chosenEndDate = values.period.endDate;

        if (!historyState?.autoSubmit && campaign.status === "Active") {
          const campaignWithNewData: NewCampaign = {
            ...campaign,
            maxLeadsPerWeek: values.maxLeadsPerWeek,
            chosenStartDate,
            chosenEndDate,
          };

          const [credits, campaignLeadCost, updatedCampaignLeadCost] =
            await Promise.all([
              context.queryClient.ensureQueryData(creditsQueryOptions),
              context.queryClient.ensureQueryData(
                getCampaignLeadCostQueryOptions(campaign),
              ),
              context.queryClient.ensureQueryData(
                getCampaignLeadCostQueryOptions(campaignWithNewData),
              ),
            ]);

          const {
            updatedCreditsNeededRestOfPeriod,
            updatedCreditsNeededNextPeriod,
          } = getCampaignUpdateCreditStats({
            credits,
            campaignLeadCost,
            updatedCampaignLeadCost,
          });

          const neededCredits = Math.max(
            updatedCreditsNeededRestOfPeriod,
            updatedCreditsNeededNextPeriod,
          );

          if (neededCredits) {
            const state = {
              routeStates: {
                [Route.id]: {
                  type: "Triggered",
                  values: values,
                  autoSubmit: false,
                },
                [BuyCreditsRoute.id]: {
                  neededCredits,
                },
              },
            } satisfies HistoryState;

            await navigate({
              replace: true,
              state,
            });

            await navigate({
              to: BuyCreditsRoute.to,
              state,
            });
            return;
          }
        }

        await updateCampaign({
          maxLeadsPerWeek: values.maxLeadsPerWeek,
          chosenStartDate,
          chosenEndDate,
        });
      }}
      setSubmitHandler={setSubmitHandler}
      autoSubmit={historyState?.autoSubmit}
    />
  );
}
